import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { authService } from '@/api';
import { setUnauthorizedHandler } from '@/api/sessionAuth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useLocalStorage('current-user', null);
  const validatingRef = useRef(false);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem('current-user');
      localStorage.removeItem('auth-token');
    } catch (e) {
      console.error('Failed to clear localStorage:', e);
    }
    setUser(null);
  }, [setUser]);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const validate = async () => {
      if (validatingRef.current) return;
      validatingRef.current = true;
      try {
        const token = localStorage.getItem('auth-token');
        if (!token) {
          logout();
          return;
        }
        const me = await authService.getMe();
        if (!cancelled) {
          setUser((prev) => (prev ? { ...prev, ...me } : me));
        }
      } catch {
        if (!cancelled) logout();
      } finally {
        validatingRef.current = false;
      }
    };

    void validate();
    return () => {
      cancelled = true;
    };
  }, []); // validate stored session once on app load

  const value = useMemo(() => {
    const isAuthenticated = user !== null;

    const login = async (email, password) => {
      const appUser = await authService.login({ email, password });
      setUser(appUser);
      return appUser;
    };

    const updateCurrentUser = (updates) => {
      setUser((prev) => (prev ? { ...prev, ...updates } : prev));
    };

    const hasRole = (role) => user?.role === role;

    const hasAnyRole = (roles) => user !== null && roles.includes(user.role);

    return {
      user,
      isAuthenticated,
      login,
      logout,
      updateCurrentUser,
      hasRole,
      hasAnyRole,
    };
  }, [user, setUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
